import { EditorialPageHero } from "@/components/sections/EditorialPageHero";
import { useParams, Navigate } from "@/lib/router-compat";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Globe, Target, TrendingUp, Users, Shield, MapPin, HelpCircle } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";

// Coordenadas geo (centro de ciudad) — usadas en LocalBusiness schema para GEO/Local SEO
export const cityGeo: Record<string, { lat: number; lng: number; postalCode?: string }> = {
  madrid: { lat: 40.4168, lng: -3.7038, postalCode: '28001' },
  barcelona: { lat: 41.3874, lng: 2.1686, postalCode: '08001' },
  valencia: { lat: 39.4699, lng: -0.3763, postalCode: '46001' },
  sevilla: { lat: 37.3891, lng: -5.9845, postalCode: '41001' },
  bilbao: { lat: 43.2630, lng: -2.9350, postalCode: '48001' },
  malaga: { lat: 36.7213, lng: -4.4214, postalCode: '29001' },
  zaragoza: { lat: 41.6488, lng: -0.8891, postalCode: '50001' },
  alicante: { lat: 38.3452, lng: -0.4810, postalCode: '03001' },
  murcia: { lat: 37.9922, lng: -1.1307, postalCode: '30001' },
  valladolid: { lat: 41.6523, lng: -4.7245, postalCode: '47001' },
  granada: { lat: 37.1773, lng: -3.5986, postalCode: '18001' },
  asturias: { lat: 43.3614, lng: -5.8593, postalCode: '33001' },
};

// Contenido extendido específico por ciudad (contexto local + FAQs)
// Permite GEO/AI Overviews: respuestas directas, datos verificables, FAQ schema.
export interface CityProfile {
  context: { heading: string; paragraphs: string[] };
  faqs: Array<{ question: string; answer: string }>;
}

export const cityProfiles: Record<string, CityProfile> = {
  madrid: {
    context: {
      heading: 'El contexto digital de Madrid en 2026',
      paragraphs: [
        'Madrid es el mayor mercado digital de habla hispana: capital política, financiera y tecnológica, con más de 3,3 millones de habitantes en la ciudad y 6,8 millones en el área metropolitana (Alcobendas, Pozuelo, Las Rozas, Majadahonda, Getafe, Móstoles, Alcalá de Henares). Cada mes se realizan decenas de miles de búsquedas con intención comercial en sectores como legal, salud, ecommerce, inmobiliario, hostelería premium y servicios B2B. Quien no compite en el top 3 de Google en Madrid, no existe.',
        'El problema en Madrid no es la demanda —es la saturación de oferta. Aparecen agencias SEO nuevas cada semana y la mayoría aplica plantillas genéricas: keyword research superficial, contenido producido en masa con IA sin revisar, link building de baja calidad y reporting basado en posiciones que no convierten. El comprador madrileño es exigente, compara mucho y descarta rápido: si tu web tarda más de 2 segundos, si tu contenido no responde con precisión o si tu autoridad de dominio es baja, Google te aparta del juego.',
        'En SEOEVOLUTION trabajamos SEO Web en Madrid con enfoque premium absoluto: auditoría técnica completa (Core Web Vitals, schema, indexación, canibalización), keyword research específico por distrito y sector, contenido optimizado para Google y motores generativos (ChatGPT, Perplexity, Gemini, Atlas), link building con medios autoritativos nacionales y reporting mensual de facturación —no de impresiones ni de clics vanidosos.',
        'No prometemos posiciones. Ejecutamos el trabajo profesional que las consigue. Y aplicamos exclusividad por sector y zona estricta: si trabajamos con tu despacho, tu clínica o tu ecommerce en Madrid, tu competidor directo no puede contratarnos.',
      ],
    },
    faqs: [
      {
        question: '¿Cuánto cuesta una agencia SEO en Madrid?',
        answer: 'En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia. La inversión para SEO Web en Madrid depende del nivel competitivo del sector, el estado de la web, el número de búsquedas objetivo y el volumen de contenido necesario. Alcance e inversión se concretan tras un diagnóstico técnico real, con compromiso mínimo de 6 meses.',
      },
      {
        question: '¿En cuánto tiempo veré resultados de SEO en Madrid?',
        answer: 'Los primeros movimientos en SERPs aparecen entre la semana 10 y la 14 en Madrid (más lento que en ciudades secundarias por la saturación competitiva). Resultados consolidados —tráfico orgánico recurrente y leads cualificados— requieren 6-9 meses. Para keywords muy competidas como "abogado madrid", "clínica dental madrid" o "agencia seo madrid" planificamos 9-15 meses. Por eso exigimos compromiso mínimo de 6 meses.',
      },
      {
        question: '¿Trabajáis SEO Web solo en Madrid capital o también en el área metropolitana?',
        answer: 'Trabajamos toda la Comunidad de Madrid: zona norte (Alcobendas, San Sebastián de los Reyes, Tres Cantos), zona oeste (Pozuelo, Las Rozas, Majadahonda, Boadilla), zona sur (Getafe, Móstoles, Leganés, Alcorcón, Fuenlabrada) y Corredor del Henares (Alcalá, Torrejón, Coslada). La estrategia SEO se diseña por distrito o municipio cuando el negocio es local, y a nivel autonómico cuando es B2B o servicios profesionales.',
      },
      {
        question: '¿Qué diferencia hay entre SEO Web y SEO Local en Madrid?',
        answer: 'SEO Web posiciona tu web en los resultados orgánicos de Google (la lista azul). SEO Local te posiciona en Google Maps y en el pack local (las 3 fichas con estrellas). Si tienes oficina, clínica o local físico en Madrid y recibes clientes, necesitas ambos. Si vendes servicios B2B o ecommerce a nivel nacional desde Madrid, prioriza SEO Web. Nuestro servicio incluye ambas dimensiones cuando el modelo de negocio lo requiere.',
      },
      {
        question: '¿Por qué SEOEVOLUTION y no una agencia grande de Madrid?',
        answer: 'Por tres razones: (1) trabajamos con exclusividad por sector y zona —tu competidor directo no nos contrata—; (2) plazas limitadas para asegurar atención senior y no rotación de juniors; (3) nuestro reporting mide facturación generada, no impresiones ni clics vanidosos. Las agencias grandes de Madrid llevan 50-100 clientes en paralelo con equipos junior y plantillas. Nosotros hacemos lo contrario: pocos clientes, trabajo profundo, resultados verificables.',
      },
      {
        question: '¿Garantizáis aparecer en el top 3 de Google para "agencia SEO Madrid"?',
        answer: 'No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren en un mercado tan competido como Madrid: auditoría completa, contenido de autoridad, link building con medios nacionales, optimización técnica continua y reporting transparente cada mes. Las posiciones llegan como consecuencia del trabajo bien hecho, no como promesa de venta.',
      },
    ],
  },
  barcelona: {
    context: {
      heading: 'El contexto digital de Barcelona en 2026',
      paragraphs: [
        'Barcelona es el segundo mercado digital de España y el principal hub tecnológico del sur de Europa: capital de Catalunya, sede del Mobile World Congress, con un ecosistema startup, ecommerce y SaaS que la sitúa entre las 5 ciudades europeas con más inversión tech. Más de 1,6 millones de habitantes en la ciudad y 5,7 millones en el área metropolitana (Hospitalet, Badalona, Sant Cugat, Terrassa, Sabadell, Mataró). El comprador barcelonés es exigente, comparador, bilingüe castellano-catalán, y descarta en segundos a las empresas que no aparecen en las primeras posiciones.',
        'El problema en Barcelona no es la falta de demanda —es la dificultad de la competencia. Sectores como turismo, hostelería premium, salud, legal, moda, arquitectura, ecommerce y agencias creativas concentran centenares de competidores luchando por las mismas keywords. Y muchas agencias SEO locales aplican un enfoque generalista, sin entender ni el bilingüismo, ni la fuerte componente de marca personal del mercado catalán, ni la importancia de los medios de comunicación locales para el link building.',
        'En SEOEVOLUTION trabajamos SEO Web en Barcelona con enfoque premium absoluto: auditoría técnica completa (Core Web Vitals, schema multilingüe ES/CA, indexación, canibalización), keyword research por barrio y por idioma cuando el negocio lo requiere (Eixample, Gràcia, Sant Martí, Sarrià), contenido optimizado para Google y motores generativos (ChatGPT, Perplexity, Gemini), link building con medios autoritativos catalanes y nacionales, y reporting mensual de facturación —no de vanity metrics.',
        'No prometemos posiciones. Ejecutamos el trabajo profesional que las consigue. Y aplicamos exclusividad por sector y zona estricta: si trabajamos con tu despacho, tu clínica o tu ecommerce en Barcelona, tu competidor directo no puede contratarnos.',
      ],
    },
    faqs: [
      {
        question: '¿Cuánto cuesta una agencia SEO en Barcelona?',
        answer: 'En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia. La inversión para SEO Web en Barcelona depende del nivel competitivo del sector, el estado de la web, el número de búsquedas objetivo y el volumen de contenido necesario. Alcance e inversión se concretan tras un diagnóstico técnico real, con compromiso mínimo de 6 meses.',
      },
      {
        question: '¿En cuánto tiempo veré resultados de SEO en Barcelona?',
        answer: 'Los primeros movimientos en SERPs aparecen entre la semana 10 y la 14 en Barcelona (similar a Madrid por saturación competitiva). Resultados consolidados —tráfico orgánico recurrente y leads cualificados— requieren 6-9 meses. Para keywords muy competidas como "agencia seo barcelona", "abogado barcelona" o "clínica dental barcelona" planificamos 9-15 meses. Por eso exigimos compromiso mínimo de 6 meses: el SEO sin tiempo no funciona, da igual la agencia.',
      },
      {
        question: '¿Trabajáis SEO Web solo en Barcelona ciudad o también en el área metropolitana?',
        answer: 'Trabajamos toda la provincia: Barcelona ciudad (todos los distritos), Baix Llobregat (Hospitalet, Sant Boi, Cornellà, Sant Cugat), Vallès Occidental (Terrassa, Sabadell, Rubí), Vallès Oriental (Granollers, Mollet), Maresme (Mataró, Premià) y Barcelonès Nord (Badalona, Santa Coloma). La estrategia se diseña por barrio o municipio cuando el negocio es local, y a nivel provincia o Catalunya cuando es B2B, ecommerce o servicios profesionales.',
      },
      {
        question: '¿Trabajáis SEO en catalán además de en castellano?',
        answer: 'Sí. Para negocios cuyo público objetivo es predominantemente catalán o cuya marca tiene fuerte arraigo local, implementamos estrategia bilingüe con estructura hreflang correcta, contenido nativo en catalán (no traducción automática) y schema multilingüe. El SEO en catalán abre acceso a búsquedas con menor competencia y mayor afinidad cultural, especialmente en sectores como gastronomía, cultura, turismo, salud y servicios profesionales locales.',
      },
      {
        question: '¿Por qué SEOEVOLUTION y no una agencia grande de Barcelona?',
        answer: 'Por tres razones: (1) trabajamos con exclusividad por sector y zona —tu competidor directo no nos contrata—; (2) plazas limitadas para asegurar trabajo senior y no rotación de juniors; (3) nuestro reporting mide facturación generada, no impresiones ni clics vanidosos. Las agencias grandes de Barcelona llevan 50-100 clientes en paralelo con equipos junior. Nosotros hacemos lo contrario: pocos clientes, trabajo profundo, resultados verificables.',
      },
      {
        question: '¿Garantizáis aparecer en el top 3 de Google para "agencia SEO Barcelona"?',
        answer: 'No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren en un mercado tan competido como Barcelona: auditoría completa, contenido de autoridad bilingüe cuando aplica, link building con medios catalanes y nacionales, optimización técnica continua y reporting transparente cada mes. Las posiciones llegan como consecuencia del trabajo, no como promesa.',
      },
    ],
  },
  bilbao: {
    context: {
      heading: 'El contexto digital de Bilbao en 2026',
      paragraphs: [
        'Bilbao concentra el mayor PIB per cápita del Cantábrico y un tejido empresarial dominado por industria, servicios financieros, ingeniería y un ecosistema startup que crece en el entorno de Zorrotzaurre y BAT (Bilbao Berrikuntza Faktoria). El comprador vasco busca en Google con intención clara: investiga, compara y contrata —pero solo a empresas que aparecen en las tres primeras posiciones. Si tu web no compite arriba, ni siquiera entras en la conversación.',
        'El problema en Bilbao no es la falta de demanda: cada mes se realizan más de 720 búsquedas para "agencia SEO Bilbao", 170 para "SEO en Bilbao" y cientos más para variantes long tail por sector (clínicas, despachos, ingenierías, hostelería). El problema es que las agencias generalistas de Madrid o Barcelona aplican estrategias genéricas sin entender la dinámica del mercado vasco: la fuerte componente B2B, el bilingüismo castellano-euskera, la importancia del boca a boca local y la concentración geográfica entre Bilbao, Getxo, Barakaldo y la Margen Izquierda.',
        'En SEOEVOLUTION trabajamos SEO Web en Bilbao con enfoque premium absoluto: auditoría técnica completa (Core Web Vitals, schema, indexación), keyword research específico para el comprador vasco, contenido orientado a respuestas directas para Google y los motores generativos (ChatGPT, Perplexity, Gemini), link building con dominios autoritativos del País Vasco y reporting mensual de facturación —no de impresiones.',
        'No prometemos posiciones. Ejecutamos el trabajo profesional que las consigue. Y solo aceptamos un cliente por sector en Bilbao: si trabajamos con tu despacho de abogados, tu clínica dental o tu ingeniería, tu competidor directo no puede contratarnos.',
      ],
    },
    faqs: [
      {
        question: '¿Cuánto cuesta una agencia SEO en Bilbao?',
        answer: 'En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia. La inversión para SEO Web en Bilbao depende del nivel competitivo del sector, el estado de la web, el número de búsquedas objetivo y el volumen de contenido necesario. Alcance e inversión se concretan tras un diagnóstico técnico real, con compromiso mínimo de 6 meses.',
      },
      {
        question: '¿En cuánto tiempo veré resultados de SEO en Bilbao?',
        answer: 'Los primeros movimientos en SERPs aparecen entre la semana 8 y la 12. Resultados consolidados (tráfico orgánico recurrente y leads cualificados) requieren 5-7 meses. Para keywords muy competidas como "agencia seo bilbao" o "abogado bilbao", planificamos 9-12 meses. Por eso exigimos compromiso mínimo de 6 meses: el SEO sin tiempo no funciona, da igual la agencia.',
      },
      {
        question: '¿Trabajáis SEO Web solo en Bilbao o también en otras ciudades del País Vasco?',
        answer: 'Trabajamos toda Bizkaia (Getxo, Barakaldo, Portugalete, Sestao, Leioa, Durango), Álava (Vitoria-Gasteiz) y Gipuzkoa (San Sebastián, Irún). La estrategia SEO se diseña a nivel ciudad principal y se expande a área metropolitana cuando el sector lo justifica. Cada proyecto incluye análisis geográfico de la demanda.',
      },
      {
        question: '¿Qué diferencia hay entre SEO Web y SEO Local en Bilbao?',
        answer: 'SEO Web posiciona tu web en búsquedas orgánicas de Google (la lista azul de resultados). SEO Local te posiciona en el mapa de Google y en el pack local (las 3 fichas con estrellas). Si tienes oficina física en Bilbao y recibes clientes, necesitas ambos. Si vendes servicios B2B a nivel nacional desde Bilbao, prioriza SEO Web. Nuestro servicio incluye ambas dimensiones cuando el modelo de negocio lo requiere.',
      },
      {
        question: '¿Por qué SEOEVOLUTION y no una agencia SEO de Madrid o Barcelona?',
        answer: 'Por tres razones: (1) conocemos el comprador vasco —su tono, su forma de buscar, su exigencia—; (2) construimos enlaces desde medios y directorios del País Vasco que ninguna agencia de fuera puede conseguir; (3) trabajamos con exclusividad por sector y zona: tu competidor directo no nos contrata. Las agencias grandes de Madrid llevan a 50 clientes en paralelo; nosotros plazas limitadas.',
      },
      {
        question: '¿Garantizáis aparecer en el top 3 de Google para "agencia SEO Bilbao"?',
        answer: 'No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren: auditoría completa, contenido de autoridad, enlaces de calidad, optimización técnica continua y reporting transparente. Las posiciones llegan como consecuencia del trabajo bien hecho.',
      },
    ],
  },
  valencia: {
    context: {
      heading: 'El contexto digital de Valencia en 2026',
      paragraphs: [
        'Valencia es la tercera ciudad de España (800.000 habitantes, 1,7M en el área metropolitana: Torrent, Paterna, Mislata, Alaquàs, Aldaia, Burjassot, Sagunto) y un mercado digital en pleno auge: hub tecnológico tras la llegada de Marina de Empresas, Lanzadera y la consolidación del Valencia Digital Summit, expansión del ecommerce de moda y alimentación, y un sector turístico premium en crecimiento sostenido. Cada mes se realizan miles de búsquedas con intención comercial en sectores como legal, salud, hostelería, ecommerce y servicios B2B.',
        'El problema en Valencia no es la demanda —es la oferta saturada de agencias generalistas que aplican plantillas: keyword research superficial, contenido producido en masa con IA sin revisar, link building de baja calidad y reporting de impresiones que no se traduce en facturación. El comprador valenciano es exigente, comparador y bilingüe castellano-valenciano según sector y zona (l\'Horta, la Safor, la Ribera). Si tu web no aparece en el top 3 y no carga rápido, te descarta en segundos.',
        'En SEOEVOLUTION trabajamos SEO Web en Valencia con enfoque premium absoluto: auditoría técnica completa (Core Web Vitals, schema, indexación, canibalización), keyword research específico por barrio y sector, contenido optimizado para Google y motores generativos (ChatGPT, Perplexity, Gemini, Atlas), link building con medios autoritativos valencianos y nacionales, y reporting mensual de facturación —no de vanity metrics.',
        'No prometemos posiciones. Ejecutamos el trabajo profesional que las consigue. Y aplicamos exclusividad por sector y zona estricta: si trabajamos con tu despacho, tu clínica o tu ecommerce en Valencia, tu competidor directo no puede contratarnos.',
      ],
    },
    faqs: [
      {
        question: '¿Cuánto cuesta una agencia SEO en Valencia?',
        answer: 'En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia. La inversión para SEO Web en Valencia depende del nivel competitivo del sector, el estado de la web, el número de búsquedas objetivo y el volumen de contenido necesario. Alcance e inversión se concretan tras un diagnóstico técnico real, con compromiso mínimo de 6 meses.',
      },
      {
        question: '¿En cuánto tiempo veré resultados de SEO en Valencia?',
        answer: 'Los primeros movimientos en SERPs aparecen entre la semana 8 y la 12 en Valencia (algo más rápido que en Madrid o Barcelona por menor saturación). Resultados consolidados —tráfico orgánico recurrente y leads cualificados— requieren 5-8 meses. Para keywords muy competidas como "agencia seo valencia", "abogado valencia" o "clínica dental valencia" planificamos 8-12 meses. Por eso exigimos compromiso mínimo de 6 meses.',
      },
      {
        question: '¿Trabajáis SEO Web solo en Valencia capital o también en el área metropolitana?',
        answer: 'Trabajamos toda la provincia: Valencia ciudad (todos los distritos), l\'Horta Nord (Burjassot, Godella, Rocafort), l\'Horta Sud (Torrent, Mislata, Alaquàs, Aldaia, Picassent), Camp de Túria (Paterna, Riba-roja) y comarcas costeras (Sagunto, Cullera, Gandía). La estrategia se diseña por barrio o municipio cuando el negocio es local, y a nivel provincia o Comunitat cuando es B2B, ecommerce o servicios profesionales.',
      },
      {
        question: '¿Trabajáis SEO en valenciano además de en castellano?',
        answer: 'Sí, cuando el público objetivo lo requiere. Implementamos estrategia bilingüe con estructura hreflang correcta, contenido nativo en valenciano (no traducción automática) y schema multilingüe. Es especialmente recomendable para negocios con fuerte arraigo local en comarcas como la Safor, la Ribera Alta o l\'Horta, y para sectores como gastronomía, cultura, turismo y servicios profesionales locales.',
      },
      {
        question: '¿Por qué SEOEVOLUTION y no una agencia local de Valencia?',
        answer: 'Por tres razones: (1) trabajamos con exclusividad por sector y zona —tu competidor directo no nos contrata—; (2) plazas limitadas para asegurar atención senior y no rotación de juniors; (3) nuestro reporting mide facturación generada, no impresiones ni clics vanidosos. Muchas agencias valencianas llevan 30-60 clientes en paralelo con equipos junior. Nosotros hacemos lo contrario: pocos clientes, trabajo profundo, resultados verificables.',
      },
      {
        question: '¿Garantizáis aparecer en el top 3 de Google para "agencia SEO Valencia"?',
        answer: 'No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren: auditoría completa, contenido de autoridad, link building con medios valencianos y nacionales, optimización técnica continua y reporting transparente cada mes. Las posiciones llegan como consecuencia del trabajo bien hecho, no como promesa de venta.',
      },
    ],
  },
  sevilla: {
    context: {
      heading: 'El contexto digital de Sevilla en 2026',
      paragraphs: [
        'Sevilla es la capital andaluza y el principal hub económico del sur de España: 690.000 habitantes en la ciudad y 1,5M en el área metropolitana (Dos Hermanas, Alcalá de Guadaíra, Mairena del Aljarafe, Tomares, Bormujos, Castilleja). Concentra sede de instituciones, sector aeroespacial, ecosistema startup creciente en torno al Parque Cartuja 93, hostelería de alto nivel, ecommerce de moda flamenca y un mercado B2B de servicios profesionales muy activo. Cada mes se realizan miles de búsquedas con intención comercial.',
        'El problema en Sevilla no es la falta de demanda —es la dispersión: agencias que aplican un enfoque generalista sin entender la dinámica andaluza (fuerte componente de marca personal, importancia del boca a boca, sectores estacionales como Semana Santa y Feria de Abril) ni la concentración geográfica entre Sevilla capital y el Aljarafe. El comprador sevillano valora la cercanía y la confianza, pero exige resultados: si tu web no aparece en el top 3, no entras en su shortlist.',
        'En SEOEVOLUTION trabajamos SEO Web en Sevilla con enfoque premium absoluto: auditoría técnica completa (Core Web Vitals, schema, indexación, canibalización), keyword research específico por barrio y sector, contenido optimizado para Google y motores generativos (ChatGPT, Perplexity, Gemini, Atlas), link building con medios autoritativos andaluces y nacionales, y reporting mensual de facturación —no de vanity metrics.',
        'No prometemos posiciones. Ejecutamos el trabajo profesional que las consigue. Y aplicamos exclusividad por sector y zona estricta: si trabajamos con tu despacho, tu clínica o tu ecommerce en Sevilla, tu competidor directo no puede contratarnos.',
      ],
    },
    faqs: [
      {
        question: '¿Cuánto cuesta una agencia SEO en Sevilla?',
        answer: 'En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia. La inversión para SEO Web en Sevilla depende del nivel competitivo del sector, el estado de la web, el número de búsquedas objetivo y el volumen de contenido necesario. Alcance e inversión se concretan tras un diagnóstico técnico real, con compromiso mínimo de 6 meses.',
      },
      {
        question: '¿En cuánto tiempo veré resultados de SEO en Sevilla?',
        answer: 'Los primeros movimientos en SERPs aparecen entre la semana 8 y la 12 en Sevilla. Resultados consolidados —tráfico orgánico recurrente y leads cualificados— requieren 5-8 meses. Para keywords muy competidas como "agencia seo sevilla", "abogado sevilla" o "clínica dental sevilla" planificamos 8-12 meses. Por eso exigimos compromiso mínimo de 6 meses: el SEO sin tiempo no funciona, da igual la agencia.',
      },
      {
        question: '¿Trabajáis SEO Web solo en Sevilla capital o también en el área metropolitana?',
        answer: 'Trabajamos toda la provincia: Sevilla ciudad (todos los distritos), Aljarafe (Mairena, Tomares, Bormujos, Castilleja, Espartinas, Bollullos), Sur (Dos Hermanas, Alcalá de Guadaíra, Utrera) y comarcas (Écija, Carmona, Lebrija). La estrategia se diseña por barrio o municipio cuando el negocio es local, y a nivel provincia o Andalucía Occidental cuando es B2B, ecommerce o servicios profesionales.',
      },
      {
        question: '¿Qué diferencia hay entre SEO Web y SEO Local en Sevilla?',
        answer: 'SEO Web posiciona tu web en los resultados orgánicos de Google (la lista azul). SEO Local te posiciona en Google Maps y en el pack local (las 3 fichas con estrellas). Si tienes oficina, clínica o local físico en Sevilla y recibes clientes, necesitas ambos. Si vendes servicios B2B o ecommerce a nivel nacional desde Sevilla, prioriza SEO Web. Nuestro servicio incluye ambas dimensiones cuando el modelo de negocio lo requiere.',
      },
      {
        question: '¿Por qué SEOEVOLUTION y no una agencia local de Sevilla?',
        answer: 'Por tres razones: (1) trabajamos con exclusividad por sector y zona —tu competidor directo no nos contrata—; (2) plazas limitadas para asegurar atención senior y no rotación de juniors; (3) nuestro reporting mide facturación generada, no impresiones ni clics vanidosos. Muchas agencias sevillanas llevan 30-50 clientes en paralelo con equipos junior. Nosotros hacemos lo contrario: pocos clientes, trabajo profundo, resultados verificables.',
      },
      {
        question: '¿Garantizáis aparecer en el top 3 de Google para "agencia SEO Sevilla"?',
        answer: 'No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren: auditoría completa, contenido de autoridad, link building con medios andaluces y nacionales, optimización técnica continua y reporting transparente cada mes. Las posiciones llegan como consecuencia del trabajo, no como promesa de venta.',
      },
    ],
  },
  malaga: {
    context: {
      heading: 'El contexto digital de Málaga en 2026',
      paragraphs: [
        'Málaga es el mercado digital de mayor crecimiento de España: 580.000 habitantes en la ciudad y 1,7M en la Costa del Sol (Marbella, Estepona, Fuengirola, Benalmádena, Torremolinos, Mijas, Rincón de la Victoria, Vélez-Málaga). Conocida como el nuevo Silicon Valley europeo tras la llegada de Google Safety Engineering Center, Vodafone European Innovation Hub, sedes de TDK, Citi, Globant y el polo tecnológico del PTA (Parque Tecnológico de Andalucía). Suma a esto un ecosistema startup en plena explosión, un mercado inmobiliario premium internacional y un turismo de alto poder adquisitivo.',
        'El problema en Málaga no es la demanda —se ha multiplicado—; es que la competencia se ha sofisticado al mismo ritmo. Agencias internacionales, startups SaaS, ecommerce de moda y lujo, clínicas premium internacionales, despachos legales especializados en cliente extranjero. El comprador malagueño y de la Costa del Sol es bilingüe (castellano-inglés) en muchos sectores, comparador, exigente y descarta a empresas que no aparecen en el top 3. Si tu web no carga rápido y no responde con autoridad, has perdido la oportunidad.',
        'En SEOEVOLUTION trabajamos SEO Web en Málaga con enfoque premium absoluto: auditoría técnica completa (Core Web Vitals, schema multilingüe ES/EN cuando aplica, indexación, canibalización), keyword research específico por zona y sector, contenido optimizado para Google y motores generativos (ChatGPT, Perplexity, Gemini, Atlas), link building con medios autoritativos andaluces, nacionales e internacionales, y reporting mensual de facturación.',
        'No prometemos posiciones. Ejecutamos el trabajo profesional que las consigue. Y aplicamos exclusividad por sector y zona estricta: si trabajamos con tu clínica, tu inmobiliaria o tu ecommerce en Málaga o Costa del Sol, tu competidor directo no puede contratarnos.',
      ],
    },
    faqs: [
      {
        question: '¿Cuánto cuesta una agencia SEO en Málaga?',
        answer: 'En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia. La inversión para SEO Web en Málaga depende del nivel competitivo del sector, el estado de la web, el número de búsquedas objetivo y el volumen de contenido necesario. Alcance e inversión se concretan tras un diagnóstico técnico real, con compromiso mínimo de 6 meses.',
      },
      {
        question: '¿En cuánto tiempo veré resultados de SEO en Málaga?',
        answer: 'Los primeros movimientos en SERPs aparecen entre la semana 10 y la 14 en Málaga (la saturación competitiva crece mes a mes). Resultados consolidados —tráfico orgánico recurrente y leads cualificados— requieren 6-9 meses. Para keywords muy competidas como "agencia seo malaga", "abogado marbella" o "inmobiliaria costa del sol" planificamos 9-15 meses. Por eso exigimos compromiso mínimo de 6 meses.',
      },
      {
        question: '¿Trabajáis SEO Web solo en Málaga capital o también en la Costa del Sol?',
        answer: 'Trabajamos toda la provincia y Costa del Sol: Málaga ciudad (todos los distritos), Costa del Sol Occidental (Torremolinos, Benalmádena, Fuengirola, Mijas, Marbella, Estepona, Manilva), Costa del Sol Oriental (Rincón de la Victoria, Vélez-Málaga, Nerja, Torre del Mar) e interior (Antequera, Ronda, Coín). La estrategia se diseña por zona o municipio cuando el negocio es local, y a nivel provincia o Andalucía cuando es B2B, ecommerce o servicios profesionales.',
      },
      {
        question: '¿Trabajáis SEO en inglés además de en castellano para clientes internacionales?',
        answer: 'Sí. Para negocios cuyo público objetivo es predominantemente internacional (inmobiliario premium en Marbella/Estepona, clínicas de salud privadas, despachos legales especializados en extranjeros, hoteles boutique), implementamos estrategia bilingüe ES/EN con estructura hreflang correcta, contenido nativo en ambos idiomas y schema multilingüe. El SEO en inglés abre acceso a búsquedas con menor competencia local y mayor poder adquisitivo.',
      },
      {
        question: '¿Por qué SEOEVOLUTION y no una agencia local de Málaga?',
        answer: 'Por tres razones: (1) trabajamos con exclusividad por sector y zona —tu competidor directo no nos contrata—; (2) plazas limitadas para asegurar atención senior y no rotación de juniors; (3) nuestro reporting mide facturación generada, no impresiones ni clics vanidosos. Muchas agencias malagueñas llevan 40-80 clientes en paralelo con equipos junior. Nosotros hacemos lo contrario: pocos clientes, trabajo profundo, resultados verificables.',
      },
      {
        question: '¿Garantizáis aparecer en el top 3 de Google para "agencia SEO Málaga"?',
        answer: 'No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren en un mercado tan competido como Málaga: auditoría completa, contenido de autoridad bilingüe cuando aplica, link building con medios andaluces, nacionales e internacionales, optimización técnica continua y reporting transparente cada mes.',
      },
    ],
  },

};


export interface CityData {
  name: string;
  province: string;
  description: string;
  population: string;
  keywords: string[];
}

export const citiesData: Record<string, CityData> = {
  'madrid': {
    name: 'Madrid',
    province: 'Madrid',
    description: 'la capital y epicentro digital de España',
    population: '3.3 millones',
    keywords: ['SEO Web Madrid', 'posicionamiento web Madrid', 'agencia SEO Madrid']
  },
  'barcelona': {
    name: 'Barcelona',
    province: 'Barcelona',
    description: 'la ciudad condal y referente tecnológico europeo',
    population: '1.6 millones',
    keywords: ['SEO Web Barcelona', 'diseño web SEO Barcelona', 'marketing digital Barcelona']
  },
  'valencia': {
    name: 'Valencia',
    province: 'Valencia',
    description: 'la ciudad mediterránea con proyección internacional',
    population: '800 mil',
    keywords: ['SEO Web Valencia', 'posicionamiento web Valencia', 'agencia SEO Valencia']
  },
  'sevilla': {
    name: 'Sevilla',
    province: 'Sevilla',
    description: 'capital andaluza y hub de innovación del sur',
    population: '690 mil',
    keywords: ['SEO Web Sevilla', 'posicionamiento web Sevilla', 'marketing digital Sevilla']
  },
  'bilbao': {
    name: 'Bilbao',
    province: 'Vizcaya',
    description: 'motor económico del norte y referente empresarial',
    population: '345 mil',
    keywords: ['SEO Web Bilbao', 'posicionamiento web Bilbao', 'agencia SEO Bilbao']
  },
  'toledo': {
    name: 'Toledo',
    province: 'Toledo',
    description: 'la ciudad imperial, cuna de historia y cultura con un tejido empresarial en crecimiento digital',
    population: '85 mil',
    keywords: ['SEO Web Toledo', 'posicionamiento web Toledo', 'agencia SEO Toledo']
  },
  'guadalajara': {
    name: 'Guadalajara',
    province: 'Guadalajara',
    description: 'una ciudad en expansión conectada al Corredor del Henares y con gran potencial digital',
    population: '87 mil',
    keywords: ['SEO Web Guadalajara', 'posicionamiento web Guadalajara', 'agencia SEO Guadalajara']
  },
  'avila': {
    name: 'Ávila',
    province: 'Ávila',
    description: 'ciudad patrimonio con un tejido empresarial que necesita visibilidad digital',
    population: '57 mil',
    keywords: ['SEO Web Ávila', 'posicionamiento web Ávila', 'agencia SEO Ávila']
  },
  'segovia': {
    name: 'Segovia',
    province: 'Segovia',
    description: 'ciudad monumental con empresas que buscan destacar en el entorno digital',
    population: '52 mil',
    keywords: ['SEO Web Segovia', 'posicionamiento web Segovia', 'agencia SEO Segovia']
  },
  'cuenca': {
    name: 'Cuenca',
    province: 'Cuenca',
    description: 'ciudad con encanto y empresas que merecen ser encontradas en Google',
    population: '54 mil',
    keywords: ['SEO Web Cuenca', 'posicionamiento web Cuenca', 'agencia SEO Cuenca']
  },
  'granada': {
    name: 'Granada',
    province: 'Granada',
    description: 'ciudad universitaria y turística con un mercado digital en plena ebullición',
    population: '232 mil',
    keywords: ['SEO Web Granada', 'posicionamiento web Granada', 'agencia SEO Granada']
  },
  'leon': {
    name: 'León',
    province: 'León',
    description: 'capital del viejo reino con empresas que buscan conquistar Google',
    population: '124 mil',
    keywords: ['SEO Web León', 'posicionamiento web León', 'agencia SEO León']
  },
  'valladolid': {
    name: 'Valladolid',
    province: 'Valladolid',
    description: 'capital de Castilla y León con un ecosistema empresarial fuerte y creciente',
    population: '298 mil',
    keywords: ['SEO Web Valladolid', 'posicionamiento web Valladolid', 'agencia SEO Valladolid']
  },
  'asturias': {
    name: 'Asturias',
    province: 'Asturias',
    description: 'el Principado con empresas que necesitan presencia digital de primer nivel',
    population: '1 millón',
    keywords: ['SEO Web Asturias', 'posicionamiento web Asturias', 'agencia SEO Asturias']
  },
  'malaga': {
    name: 'Málaga',
    province: 'Málaga',
    description: 'la capital de la Costa del Sol y nuevo Silicon Valley europeo',
    population: '578 mil',
    keywords: ['SEO Web Málaga', 'posicionamiento web Málaga', 'agencia SEO Málaga']
  },
  'zamora': {
    name: 'Zamora',
    province: 'Zamora',
    description: 'ciudad románica con negocios que buscan proyección digital',
    population: '60 mil',
    keywords: ['SEO Web Zamora', 'posicionamiento web Zamora', 'agencia SEO Zamora']
  },
  'lugo': {
    name: 'Lugo',
    province: 'Lugo',
    description: 'la ciudad amurallada con empresas que quieren romper barreras digitales',
    population: '98 mil',
    keywords: ['SEO Web Lugo', 'posicionamiento web Lugo', 'agencia SEO Lugo']
  },
  'murcia': {
    name: 'Murcia',
    province: 'Murcia',
    description: 'la huerta de Europa con un sector empresarial que necesita visibilidad online',
    population: '460 mil',
    keywords: ['SEO Web Murcia', 'posicionamiento web Murcia', 'agencia SEO Murcia']
  },
  'zaragoza': {
    name: 'Zaragoza',
    province: 'Zaragoza',
    description: 'capital aragonesa y nodo logístico con gran potencial digital',
    population: '675 mil',
    keywords: ['SEO Web Zaragoza', 'posicionamiento web Zaragoza', 'agencia SEO Zaragoza']
  },
  'alicante': {
    name: 'Alicante',
    province: 'Alicante',
    description: 'ciudad mediterránea con un ecosistema empresarial turístico y tecnológico en auge',
    population: '337 mil',
    keywords: ['SEO Web Alicante', 'posicionamiento web Alicante', 'agencia SEO Alicante']
  }
};

export default function SeoWebCiudad() {
  const { ciudad } = useParams<{ ciudad: string }>();
  const ciudadLower = ciudad?.toLowerCase();
  const cityData = ciudadLower ? citiesData[ciudadLower] : null;

  if (!cityData) {
    return <Navigate to="/servicios/seo-web-ia" replace />;
  }

  // Normaliza la URL a minúsculas para evitar canibalización (/Bilbao vs /bilbao)
  if (ciudad !== ciudadLower) {
    return <Navigate to={`/servicios/seo-web/${ciudadLower}`} replace />;
  }

  const canonicalUrl = `https://agenciaseoevolution.com/servicios/seo-web/${ciudadLower}`;
  const geo = cityGeo[ciudadLower!];
  const profile = cityProfiles[ciudadLower!];

  // Schema LocalBusiness enriquecido con coordenadas (mejora GEO + Local SEO)
  const businessSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${canonicalUrl}#business`,
    "name": `SEOEVOLUTION — Agencia SEO Web en ${cityData.name}`,
    "description": `Agencia SEO Web premium en ${cityData.name}. Trabajo profesional para competir por las primeras posiciones de Google.`,
    "url": canonicalUrl,
    "telephone": "+34910626684",
    "email": "contacto@agenciaseoevolution.com",
    "areaServed": {
      "@type": "City",
      "name": cityData.name,
      "containedIn": { "@type": "AdministrativeArea", "name": cityData.province },
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityData.name,
      "addressRegion": cityData.province,
      "postalCode": geo?.postalCode,
      "addressCountry": "ES",
    },
    ...(geo && {
      geo: { "@type": "GeoCoordinates", latitude: geo.lat, longitude: geo.lng },
    }),
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
        "opens": "10:00",
        "closes": "17:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Friday"],
        "opens": "10:00",
        "closes": "14:00",
      },
    ],
  };

  const faqSchema = profile && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": profile.faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  };

  return (
    <>

      <div className="min-h-screen flex flex-col bg-seo-dark text-white">
        <Navbar />
        
        <main className="flex-grow pt-24">
          <div className="container mx-auto px-4 md:px-6">
            <Breadcrumbs items={[
              { label: 'Servicios', path: '/mas-servicios' },
              { label: 'SEO Web', path: '/servicios/seo-web-ia' },

              { label: cityData.name, path: `/servicios/seo-web/${ciudad}` }
            ]} />
          </div>
          
          <EditorialPageHero
            kicker={`SEO Web · ${cityData.name}`}
            eyebrow="Propuesta personalizada"
            title={<>Tu web debería ser tu mejor comercial.</>}
            highlight="Si no lo es, pierdes dinero."
            lead={`Agencia SEO especializada en ${cityData.description}.`}
            description="Auditoría técnica, arquitectura, contenido de autoridad y link building. Trabajo profesional, sin promesas de posiciones."
            primaryCta={{ label: `Solicitar diagnóstico SEO en ${cityData.name}`, href: "/agenda-consultoria" }}
            secondaryCta={{ label: "WhatsApp directo", href: "https://wa.me/34910626684" }}
            note="Compromiso mínimo 6 meses. El SEO necesita tiempo para generar resultados reales."
            video={false}
          />


          {/* Exclusividad Sectorial */}
          <section className="py-12 bg-seo-card/50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-3 bg-seo-blue/10 border border-seo-blue/20 rounded-xl px-6 py-4">
                  <Shield className="w-6 h-6 text-seo-bright flex-shrink-0" />
                  <p className="text-gray-300 text-lg">
                    <span className="text-white font-bold">Exclusividad por sector y zona:</span>{' '}
                    si trabajamos contigo, tu competidor directo no puede contratarnos
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-16 bg-seo-card/50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="text-4xl font-bold text-seo-bright mb-2">{cityData.population}</div>
                  <div className="text-gray-400">Audiencia Potencial</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-seo-bright mb-2">Élite</div>
                  <div className="text-gray-400">Trabajo profesional para competir por las primeras posiciones</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-seo-bright mb-2">+400%</div>
                  <div className="text-gray-400">Aumento en Tráfico</div>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-16">
            <div className="container mx-auto px-4 md:px-6">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                ¿Por qué elegir nuestro SEO Web en <span className="text-gradient">{cityData.name}</span>?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
                <div className="bg-seo-card p-6 rounded-xl">
                  <Target className="w-12 h-12 text-seo-bright mb-4" />
                  <h3 className="text-xl font-bold mb-2">Experiencia Local</h3>
                  <p className="text-gray-400">Conocemos el mercado digital de {cityData.name} en profundidad.</p>
                </div>
                <div className="bg-seo-card p-6 rounded-xl">
                  <TrendingUp className="w-12 h-12 text-seo-bright mb-4" />
                  <h3 className="text-xl font-bold mb-2">Resultados Comprobables</h3>
                  <p className="text-gray-400">Incrementa tu visibilidad en búsquedas de {cityData.name}.</p>
                </div>
                <div className="bg-seo-card p-6 rounded-xl">
                  <Users className="w-12 h-12 text-seo-bright mb-4" />
                  <h3 className="text-xl font-bold mb-2">Tráfico Cualificado</h3>
                  <p className="text-gray-400">Atrae visitantes que realmente te interesan en {cityData.name}.</p>
                </div>
                <div className="bg-seo-card p-6 rounded-xl">
                  <Check className="w-12 h-12 text-seo-bright mb-4" />
                  <h3 className="text-xl font-bold mb-2">ROI medible</h3>
                  <p className="text-gray-400">Reportamos retorno real mes a mes. Tu inversión, en métricas de facturación.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-16 bg-seo-card/30">
            <div className="container mx-auto px-4 md:px-6">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Nuestros Servicios SEO Web en {cityData.name}
              </h2>
              <div className="max-w-3xl mx-auto space-y-6">
                {[
                  'Auditoría SEO técnica completa de tu web',
                  'Optimización SEO On-Page con IA avanzada',
                  'Estrategia de contenidos SEO para ' + cityData.name,
                  'Link Building de alta calidad',
                  'Mejora de Core Web Vitals y velocidad',
                  'Keyword Research específico para tu sector en ' + cityData.name,
                  'Optimización de conversiones (CRO)',
                  'Informes mensuales con métricas clave'
                ].map((service, index) => (
                  <div key={index} className="flex items-start bg-seo-card p-6 rounded-lg">
                    <Check className="w-6 h-6 text-seo-bright mr-4 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-300">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contexto local + FAQ (solo ciudades con perfil completo, optimizado para GEO/AI Overviews) */}
          {profile && (
            <>
              <section className="py-16 bg-seo-card/30">
                <div className="container mx-auto px-4 md:px-6">
                  <div className="max-w-3xl mx-auto">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-seo-blue/20 border border-seo-blue/30 mb-6">
                      <MapPin className="w-4 h-4 mr-2 text-seo-bright" />
                      <span className="text-sm font-medium text-seo-bright">Análisis local</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-8">
                      {profile.context.heading}
                    </h2>
                    <div className="space-y-5 text-lg text-gray-300 leading-relaxed">
                      {profile.context.paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section className="py-16">
                <div className="container mx-auto px-4 md:px-6">
                  <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-seo-blue/20 border border-seo-blue/30 mb-4">
                        <HelpCircle className="w-4 h-4 mr-2 text-seo-bright" />
                        <span className="text-sm font-medium text-seo-bright">Preguntas frecuentes</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold">
                        SEO Web en {cityData.name}: lo que más se pregunta
                      </h2>
                    </div>
                    <div className="space-y-4">
                      {profile.faqs.map((faq, i) => (
                        <details
                          key={i}
                          className="group bg-seo-card border border-seo-blue/10 rounded-xl p-6 open:border-seo-blue/30 transition-colors"
                        >
                          <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                            <h3 className="text-lg md:text-xl font-semibold text-white">
                              {faq.question}
                            </h3>
                            <span className="text-seo-bright text-2xl leading-none flex-shrink-0 group-open:rotate-45 transition-transform">
                              +
                            </span>
                          </summary>
                          <p className="mt-4 text-gray-300 leading-relaxed">
                            {faq.answer}
                          </p>
                        </details>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* CTA Section */}
          <section className="py-16">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-seo-blue/20 to-seo-purple/20 p-12 rounded-2xl border border-seo-blue/30">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  ¿Tu competencia ya nos ha llamado?
                </h2>
                <p className="text-xl text-gray-300 mb-3">
                  Solo trabajamos con un cliente por sector y zona.
                </p>
                <p className="text-sm text-gray-400 mb-8">
                  Plazas limitadas por zona · Compromiso mínimo 6 meses
                </p>
                <Link to="/agenda-consultoria">
                  <Button size="lg" className="bg-seo-blue hover:bg-seo-blue/80 text-white">
                    Solicitar diagnóstico SEO en {cityData.name} →
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
